<script setup lang="ts">
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { RotateCcw, Link, Check, Trash2, CheckCircle, Clock, XCircle, Mail } from 'lucide-vue-next'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { roleToName } from '@/components/maps/mapRoles'

const props = defineProps<{
  invites: any[]
  mapId: string
}>()

const store = useStore()

const urlFromUuid = (uuid: string) => `${location.protocol}//${window.location.host}/map-invite/${uuid}`

const copiedUuid = ref<string | null>(null)
const copyLink = (uuid: string) => {
  navigator.clipboard.writeText(urlFromUuid(uuid))
  copiedUuid.value = uuid
  setTimeout(() => {
    if (copiedUuid.value === uuid) copiedUuid.value = null
  }, 1000)
}

const resend = (uuid: string) => store.dispatch('maps/resendMapInvite', { mapId: props.mapId, uuid })
const remove = (uuid: string) => store.dispatch('maps/deleteMapInvite', { mapId: props.mapId, uuid })

const statusColor = (status: string) => {
  switch (status) {
    case 'sent':
    case 'processed':
    case 'delivered':
    case 'open':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'sending':
      return 'bg-blue-100 text-blue-800 border-blue-200'
    case 'failed':
      return 'bg-red-100 text-red-800 border-red-200'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

const statusIcon = (status: string) => {
  switch (status) {
    case 'sent':
    case 'processed':
    case 'delivered':
    case 'open':
      return CheckCircle
    case 'sending':
      return Mail
    case 'failed':
      return XCircle
    default:
      return Clock
  }
}

const statusText = (status: string) => {
  switch (status) {
    case 'sent':
    case 'processed':
    case 'delivered':
    case 'open':
      return 'Sent'
    case 'sending':
      return 'Sending'
    case 'failed':
      return 'Failed'
    case 'pending':
      return 'Pending'
    default:
      return status
  }
}

const rows = computed(() => props.invites)
</script>

<template>
  <div class="divide-y divide-border" data-testid="map-invites-list">
    <div
      v-for="invite in rows"
      :key="invite.uuid"
      class="flex items-center gap-3 py-2.5"
    >
      <div class="min-w-0 flex-1">
        <p class="text-sm font-medium truncate">{{ invite.fullname || invite.email }}</p>
        <p class="text-xs text-muted-foreground truncate">{{ invite.email || invite.phone }}</p>
      </div>

      <span class="text-xs text-muted-foreground shrink-0">{{ roleToName[invite.map_role] || invite.map_role }}</span>

      <Badge :class="`${statusColor(invite.email_status)} text-xs px-2 py-1 flex items-center gap-1 shrink-0`">
        <component :is="statusIcon(invite.email_status)" class="h-3 w-3" />
        <span>{{ statusText(invite.email_status) }}</span>
      </Badge>

      <div class="flex items-center shrink-0">
        <Button variant="ghost" size="sm" class="h-7 w-7 p-0" title="Resend invite" @click="resend(invite.uuid)">
          <RotateCcw class="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" class="h-7 w-7 p-0" title="Copy link" @click="copyLink(invite.uuid)">
          <Check v-if="copiedUuid === invite.uuid" class="h-4 w-4 text-green-600" />
          <Link v-else class="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" class="h-7 w-7 p-0" title="Remove invite" @click="remove(invite.uuid)">
          <Trash2 class="h-4 w-4 text-red-400" />
        </Button>
      </div>
    </div>
  </div>
</template>
